import { HttpErrorResponse } from '@angular/common/http';
import { AbstractControl, FormControl } from '@angular/forms';
import { Observable, throwError } from 'rxjs';

/**
 * Utility helpers for validating values and form states across the application.
 * These functions are used throughout the Angular frontend to guard null checks and simplify
 * form validation logic before data is submitted or displayed.
 */

/**
 * Determines whether a value is null or undefined.
 * This helper is used as a generic guard for optional runtime values and API payloads.
 *
 * @param value Value to inspect.
 * @returns `true` if the value is null or undefined, otherwise `false`.
 */
export function isNullOrUndefined(value: unknown): boolean {
  return value === null || value === undefined;
}

/**
 * Determines whether a string value is null, undefined, or empty.
 * This is commonly used to validate user input before form submission or display rendering.
 *
 * @param value String value to validate.
 * @returns `true` if the value is null, undefined, or empty; otherwise `false`.
 */
export function isNullEmptyOrUndefined(
  value: string | null | undefined
): boolean {
  return value === null || value === '' || value === undefined;
}

/**
 * Checks whether a form control is invalid after the user has interacted with it.
 * This helper is used to trigger validation feedback only after the user has touched or modified a field.
 *
 * @param control Angular form control to evaluate.
 * @returns `true` when the control is invalid and has been marked as dirty or touched.
 */
export function isInvalid(control: FormControl | AbstractControl): boolean {
  return control.invalid && (control.dirty || control.touched);
}

/**
 * Centralized mapping of HTTP status codes to user-facing error messages.
 * The entries are used by the global error handling utility to convert backend responses into
 * readable application messages.
 */
const ERROR_MESSAGES: Record<number, string> = {
  0: 'Network error. Please check your connection and try again.',
  400: 'Bad request',
  401: 'Invalid credentials',
  403: 'Access denied',
  404: 'Not found',
  409: 'Conflict error',
  408: 'Request timeout',
  422: 'Validation failed',
  429: 'Too many requests. Please try again later.',
  500: 'Internal server error',
  502: 'Bad gateway',
  503: 'Service unavailable',
  504: 'Gateway timeout',
};

/**
 * Converts an Angular HTTP error response into a thrown application error.
 * The function resolves the status code to a readable message and rethrows it as an error
 * observable so the calling service can display a consistent user-facing notification.
 *
 * @param error HTTP error response returned by the backend.
 * @returns An observable that throws a descriptive application error message.
 */
export function errorHandling(error: HttpErrorResponse): Observable<never> {
  const message = ERROR_MESSAGES[error.status] ?? 'An unknown error occurred';
  return throwError(() => new Error(message));
}
